/**
 * Barcode encoding and drawing. bwip-js only supplies the symbol geometry
 * (bwip-js raw()); modules are drawn as rectangles in printer-dot space, so a
 * QR module is exactly ^BQ magnification dots and a Code 128 module is exactly
 * the ^BY width.
 *
 * Ported from ns-web resources/js/components/common/label-viewer/utils/barcode-renderer.js
 * Changes from NS:
 *  - Uses raw module data instead of rendering to an offscreen canvas at 4× and
 *    rescaling with an empirical "2 px per module" factor. No DOM is needed, so
 *    it works with OffscreenCanvas and in tests.
 *  - Code 128 follows the printer's ^BC mode N rules: subset B unless the data
 *    starts with an invocation code (>9 A, >: B, >; C), with >5/>6/>7 subset
 *    switches and >8 FNC1. bwip-js on its own picks the shortest encoding
 *    (subset C for digit runs), which is narrower than what the printer prints.
 *  - The QR error-correction level from the parser (the ^FD prefix) is passed
 *    through; NS dropped it in favour of the ^BQ parameter.
 *  - Default magnification follows the printer's DPI (^BQ: 2 at 203, 3 at 300).
 *  - QR mask: bwip-js and the printer may choose different mask patterns, so
 *    module grids can differ while size, data and EC level match. That is the
 *    accuracy criterion (QA gate 1, F4: 17 of 29 grids identical vs Labelary).
 *  - NS's bwip ids 'codabar' and 'gs1databar-omni' don't exist in bwip-js; fixed.
 */

import type { BarcodeElement, LinearOptions, MatrixOptions, QrOptions } from './document-model';
import { BWIP_ENCODER, MATRIX_TYPES, STACKED_TYPES, VERIFIED_TYPES } from './barcode-types';
import type { LabelCanvasContext } from './canvas-context';

type BwipRaw = (opts: Record<string, unknown>) => Array<Record<string, unknown>>;

export interface BarcodeEncoder {
  raw: BwipRaw;
}

let bwip: BarcodeEncoder | null = null;
let loading: Promise<BarcodeEncoder> | null = null;

/**
 * Lazily load the bwip-js browser build. Safe to call repeatedly. The raw
 * encoder is on the default export: the browser build's named export "raw"
 * is BWIPP's "raw" symbology, not the encoder.
 */
export function loadBarcodeEncoder(): Promise<BarcodeEncoder> {
  if (bwip) return Promise.resolve(bwip);
  if (!loading) {
    loading = import('bwip-js/browser').then((mod) => {
      // ESM: encoder on default. CommonJS without interop (e.g. Jest): the module itself.
      const m = mod as unknown as BarcodeEncoder & { default?: BarcodeEncoder };
      bwip = m.default && typeof m.default.raw === 'function' ? m.default : m;
      return bwip;
    });
  }
  return loading;
}

/** The encoder if it has been loaded, else null (used by synchronous size estimates). */
export function loadedBarcodeEncoder(): BarcodeEncoder | null {
  return bwip;
}

export type EncodedBarcode =
  | {
      kind: 'linear';
      /** Alternating bar/space widths in modules, starting with a bar. */
      widths: number[];
      moduleWidth: number;
      barHeight: number;
      text: string | null;
      textAbove: boolean;
    }
  | {
      kind: 'matrix';
      columns: number;
      rows: number;
      /** Row-major module grid, 1 = dark. */
      modules: number[];
      moduleWidth: number;
      moduleHeight: number;
    };

export interface BarcodeSize {
  w: number;
  h: number;
}

// ---------------------------------------------------------------------------
// Code 128 (^BC) codewords
// ---------------------------------------------------------------------------

type Subset = 'A' | 'B' | 'C';
const START: Record<Subset, number> = { A: 103, B: 104, C: 105 };
const SWITCH_TO: Record<Subset, number> = { A: 101, B: 100, C: 99 };

function valueInSubset(code: number, subset: 'A' | 'B'): number | null {
  if (subset === 'B') return code >= 32 && code <= 127 ? code - 32 : null;
  if (code >= 32 && code <= 95) return code - 32;
  if (code >= 0 && code < 32) return code + 64;
  return null;
}

/**
 * Translate ^BC mode N field data into Code 128 codewords (without check digit
 * and stop, which bwip-js adds). Returns the codewords plus any problems found.
 */
export function code128ModeNCodewords(data: string): { codewords: number[]; humanReadable: string; problems: string[] } {
  const problems: string[] = [];
  let subset: Subset = 'B';
  let i = 0;
  const startCode = data.slice(0, 2);
  if (startCode === '>9') subset = 'A';
  else if (startCode === '>:') subset = 'B';
  else if (startCode === '>;') subset = 'C';
  if (startCode === '>9' || startCode === '>:' || startCode === '>;') i = 2;

  const codewords = [START[subset]];
  let human = '';
  while (i < data.length) {
    const ch = data[i];
    if (ch === '>' && i + 1 < data.length) {
      const inv = data[i + 1];
      const target: Subset | null = inv === '5' ? 'C' : inv === '6' ? 'B' : inv === '7' ? 'A' : null;
      if (target) {
        if (target !== subset) codewords.push(SWITCH_TO[target]);
        subset = target;
        i += 2;
        continue;
      }
      if (inv === '8') {
        codewords.push(102);
        i += 2;
        continue;
      }
      if (inv === '0' && subset !== 'C') {
        codewords.push(30); // '>' itself
        human += '>';
        i += 2;
        continue;
      }
      problems.push(`invocation code >${inv} is not previewed`);
      i += 2;
      continue;
    }
    if (subset === 'C') {
      const pair = data.slice(i, i + 2);
      if (/^\d\d$/.test(pair)) {
        codewords.push(parseInt(pair, 10));
        human += pair;
        i += 2;
        continue;
      }
      problems.push('subset C needs digit pairs; switched to subset B');
      codewords.push(SWITCH_TO.B);
      subset = 'B';
      continue;
    }
    const v = valueInSubset(ch.charCodeAt(0), subset);
    if (v === null) {
      problems.push(`character "${ch}" can't be encoded in Code 128 subset ${subset}`);
    } else {
      codewords.push(v);
      human += ch;
    }
    i++;
  }
  return { codewords, humanReadable: human, problems };
}

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

/** Printer default ^BQ / ^BX magnification for a resolution. */
export function defaultMagnification(dpi: number): number {
  if (dpi <= 150) return 1;
  if (dpi <= 203) return 2;
  if (dpi <= 300) return 3;
  return 6;
}

function asNumbers(v: unknown): number[] {
  return Array.isArray(v) ? (v as number[]) : [];
}

/**
 * Encode a barcode element into module geometry. Throws when bwip-js rejects
 * the data. problems collects non-fatal differences from the printer.
 */
export function encodeBarcode(
  encoder: BarcodeEncoder,
  el: BarcodeElement,
  dpi: number,
  problems: string[] = [],
): EncodedBarcode {
  const bcid = BWIP_ENCODER[el.barcodeType];
  if (!VERIFIED_TYPES.has(el.barcodeType)) {
    problems.push(`${el.barcodeType} preview hasn't been checked against a printer`);
  }

  if (MATRIX_TYPES.has(el.barcodeType) || STACKED_TYPES.has(el.barcodeType) || el.barcodeType === 'maxicode') {
    const opts: Record<string, unknown> = { bcid, text: el.data };
    let moduleWidth: number;
    let moduleHeight: number;
    if (el.barcodeType === 'qrcode') {
      const qr = el.options as QrOptions;
      opts.eclevel = qr.errorCorrection;
      if (qr.model === 1) problems.push('QR model 1 is previewed as model 2');
      moduleWidth = moduleHeight = qr.magnification > 0 ? qr.magnification : defaultMagnification(dpi);
    } else {
      const m = el.options as MatrixOptions;
      if (m.columns > 0) opts.columns = m.columns;
      if (m.rows > 0) opts.rows = m.rows;
      if (STACKED_TYPES.has(el.barcodeType)) {
        if (m.eclevel !== undefined) opts.eclevel = m.eclevel;
        moduleWidth = m.magnification;
        moduleHeight = el.h;
      } else {
        moduleWidth = moduleHeight = m.magnification > 0 ? m.magnification : defaultMagnification(dpi);
      }
    }
    const [sym] = encoder.raw(opts);
    const columns = Number(sym.pixx);
    const modules = asNumbers(sym.pixs);
    const rows = Number(sym.pixy) || Math.ceil(modules.length / columns);
    return { kind: 'matrix', columns, rows, modules, moduleWidth, moduleHeight };
  }

  const lin = el.options as LinearOptions;
  const opts: Record<string, unknown> = { bcid, text: el.data };
  let text: string | null = el.data;
  if (el.barcodeType === 'code128') {
    const mode = lin.mode ?? 'N';
    // Empty data prints the subset B start / check / stop stub in any mode.
    if (mode === 'N' || !el.data) {
      const cw = code128ModeNCodewords(el.data);
      problems.push(...cw.problems);
      opts.text = cw.codewords.map((c) => '^' + String(c).padStart(3, '0')).join('');
      opts.raw = true;
      text = cw.humanReadable;
    } else if (mode !== 'A') {
      problems.push(`^BC mode ${mode} (GS1/UCC) is previewed with automatic subsets`);
    }
  } else if (lin.ratio && lin.ratio !== 3) {
    opts.ratio = lin.ratio;
  }
  const [sym] = encoder.raw(opts);
  return {
    kind: 'linear',
    widths: asNumbers(sym.sbs),
    moduleWidth: lin.moduleWidth,
    barHeight: el.h,
    text: el.showText ? text : null,
    textAbove: lin.textAbove ?? false,
  };
}

/** Height in dots of the interpretation line under a 1D barcode (approximation). */
export function interpretationLineHeight(moduleWidth: number): number {
  return Math.max(9, Math.round(moduleWidth * 9));
}

/** Size of an encoded symbol in dots, including the interpretation line. */
export function encodedSize(enc: EncodedBarcode): BarcodeSize {
  if (enc.kind === 'matrix') return { w: enc.columns * enc.moduleWidth, h: enc.rows * enc.moduleHeight };
  const modules = enc.widths.reduce((a, b) => a + b, 0);
  const textH = enc.text ? interpretationLineHeight(enc.moduleWidth) + 2 : 0;
  return { w: modules * enc.moduleWidth, h: enc.barHeight + textH };
}

// ---------------------------------------------------------------------------
// Drawing (dot space, origin = symbol top-left)
// ---------------------------------------------------------------------------

export function drawEncoded(ctx: LabelCanvasContext, enc: EncodedBarcode, color: string): void {
  ctx.fillStyle = color;
  if (enc.kind === 'matrix') {
    // One rectangle per horizontal run of dark modules.
    for (let r = 0; r < enc.rows; r++) {
      let c = 0;
      while (c < enc.columns) {
        if (!enc.modules[r * enc.columns + c]) {
          c++;
          continue;
        }
        const start = c;
        while (c < enc.columns && enc.modules[r * enc.columns + c]) c++;
        ctx.fillRect(start * enc.moduleWidth, r * enc.moduleHeight, (c - start) * enc.moduleWidth, enc.moduleHeight);
      }
    }
    return;
  }
  const textH = enc.text ? interpretationLineHeight(enc.moduleWidth) + 2 : 0;
  const barTop = enc.text && enc.textAbove ? textH : 0;
  let x = 0;
  enc.widths.forEach((w, i) => {
    if (i % 2 === 0) ctx.fillRect(x * enc.moduleWidth, barTop, w * enc.moduleWidth, enc.barHeight);
    x += w;
  });
  if (enc.text) {
    const totalW = x * enc.moduleWidth;
    ctx.font = `normal ${interpretationLineHeight(enc.moduleWidth)}px "OCR B","Courier New",monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText(enc.text, totalW / 2, enc.textAbove ? 0 : enc.barHeight + 2);
    ctx.textAlign = 'left';
  }
}

/** Dashed placeholder for a barcode that couldn't be encoded. */
export function drawBarcodePlaceholder(ctx: LabelCanvasContext, w: number, h: number, message: string): void {
  ctx.save();
  ctx.strokeStyle = '#888';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.rect(0.5, 0.5, w - 1, h - 1);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.fillStyle = '#888';
  ctx.font = `normal ${Math.max(8, Math.min(16, h * 0.3))}px sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(message, w / 2, h / 2);
  ctx.restore();
}
